'use client';
import { useEffect, useRef } from 'react';

export interface PrepResultLike {
  prepId: string;
  embedUrl: string;
  expiresAt?: string | null;
  status: string;
}

export interface PrepEmbedSignedEvent { prepId: string; }
export interface PrepEmbedFailedEvent { prepId: string; error: string; message?: string; }

export interface PrepEmbedProps {
  prepResult: PrepResultLike;
  onSigned?: (e: PrepEmbedSignedEvent) => void;
  onFailed?: (e: PrepEmbedFailedEvent) => void;
  /** Optional CSS height. Defaults to 600px. */
  height?: string | number;
  /** Optional CSS width. Defaults to 100%. */
  width?: string | number;
  /** Override the iframe `title` attr for a11y. Defaults to "Signing surface". */
  title?: string;
  /** Restrict accepted postMessage origins. Defaults to the embed URL's origin. */
  expectedOrigin?: string;
}

/**
 * Drop-in iframe wrapper for the orchestrator side of Prep–Sign–Promote.
 * Renders the peer's signing iframe + listens for the prep-signed /
 * prep-sign-failed messages.
 *
 * ```tsx
 * const prep = await fetch('/api/prepare-portify-agreement').then(r => r.json());
 * return <PrepEmbed prepResult={prep} onSigned={({prepId}) => promote(prepId)} />;
 * ```
 *
 * Pair with `Sharing::prepare` on the PHP orchestrator. On `onSigned`, the
 * orchestrator's flow should: (1) take payment, (2) `Sharing::shareUser`
 * to create the auth-service share, (3) `Sharing::promote` to flip the
 * peer's temp record into a permanent record.
 */
export function PrepEmbed({
  prepResult,
  onSigned,
  onFailed,
  height = 600,
  width = '100%',
  title = 'Signing surface',
  expectedOrigin,
}: PrepEmbedProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // Use a ref (not state) for the latch — refs update synchronously,
  // state updates are async and would let duplicate postMessage events
  // double-fire callbacks before the state batch flushes.
  const completedRef = useRef(false);

  const computedOrigin = (() => {
    try {
      return expectedOrigin ?? new URL(prepResult.embedUrl).origin;
    } catch {
      return expectedOrigin;
    }
  })();

  useEffect(() => {
    function onMessage(ev: MessageEvent) {
      if (completedRef.current) return;
      if (computedOrigin && ev.origin !== computedOrigin) return;
      const data = ev.data;
      if (!data || typeof data !== 'object') return;
      if (data.prep_id !== prepResult.prepId) return;

      if (data.type === 'prep-signed') {
        completedRef.current = true;
        onSigned?.({ prepId: prepResult.prepId });
      } else if (data.type === 'prep-sign-failed') {
        completedRef.current = true;
        onFailed?.({
          prepId: prepResult.prepId,
          error: typeof data.error === 'string' ? data.error : 'unknown',
          message: typeof data.message === 'string' ? data.message : undefined,
        });
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [prepResult.prepId, computedOrigin, onSigned, onFailed]);

  return (
    <iframe
      ref={iframeRef}
      src={prepResult.embedUrl}
      title={title}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
        border: 0,
      }}
      sandbox="allow-scripts allow-same-origin allow-forms"
    />
  );
}
